import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ReportLibrary from './components/ReportLibrary';
import ReportGenerator from './components/ReportGenerator';
import ReportHistory from './components/ReportHistory';
import ScheduledReports from './components/ScheduledReports';
import ComplianceDeadlines from './components/ComplianceDeadlines';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FinancialReportsCenter = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('library'); // library, generator, history, scheduled, compliance

  // Mock data for report library
  const reportCategories = [
    {
      id: 'financial-statements',
      title: 'Financial Statements',
      icon: 'FileText',
      count: 8,
      reports: [
        { id: 'income-statement', name: 'Income Statement', description: 'Monthly P&L report', lastGenerated: '2025-10-10', size: '1.2MB' },
        { id: 'balance-sheet', name: 'Balance Sheet', description: 'Assets, liabilities and equity', lastGenerated: '2025-10-10', size: '890KB' },
        { id: 'cash-flow', name: 'Cash Flow Statement', description: 'Cash inflows and outflows', lastGenerated: '2025-10-09', size: '1.5MB' },
        { id: 'trial-balance', name: 'Trial Balance', description: 'General ledger summary', lastGenerated: '2025-10-08', size: '750KB' }
      ]
    },
    {
      id: 'tax-compliance',
      title: 'Tax & Compliance',
      icon: 'Shield',
      count: 6,
      reports: [
        { id: 'vat-return', name: 'VAT Return', description: 'Quarterly VAT submission', lastGenerated: '2025-10-05', size: '2.1MB' },
        { id: 'payroll-tax', name: 'Payroll Tax Report', description: 'Employee tax deductions', lastGenerated: '2025-10-01', size: '1.8MB' },
        { id: 'audit-trail', name: 'Audit Trail', description: 'Transaction audit log', lastGenerated: '2025-10-10', size: '3.2MB' },
        { id: 'compliance-checklist', name: 'Compliance Checklist', description: 'Regulatory compliance status', lastGenerated: '2025-10-07', size: '650KB' }
      ]
    },
    {
      id: 'management-reports',
      title: 'Management Reports',
      icon: 'BarChart3',
      count: 12,
      reports: [
        { id: 'budget-variance', name: 'Budget Variance Analysis', description: 'Budget vs actual comparison', lastGenerated: '2025-10-11', size: '2.5MB' },
        { id: 'department-analysis', name: 'Department Analysis', description: 'Per department financial analysis', lastGenerated: '2025-10-10', size: '3.1MB' },
        { id: 'kpi-dashboard', name: 'KPI Dashboard', description: 'Key performance indicators', lastGenerated: '2025-10-11', size: '1.9MB' },
        { id: 'profitability', name: 'Profitability Analysis', description: 'Product and service profitability', lastGenerated: '2025-10-09', size: '2.8MB' }
      ]
    }
  ];

  // Mock data for report history
  const reportHistory = [
    { id: 1, name: 'Monthly Financial Package - October 2025', type: 'Package', generatedAt: '2025-10-11 14:30:00', size: '12.5MB', status: 'completed', downloads: 3 },
    { id: 2, name: 'Budget Variance Analysis - Q3 2025', type: 'Management', generatedAt: '2025-10-11 09:15:00', size: '2.5MB', status: 'completed', downloads: 7 },
    { id: 3, name: 'Cash Flow Projection - Next 12 Months', type: 'Forecast', generatedAt: '2025-10-10 16:45:00', size: '1.8MB', status: 'completed', downloads: 2 },
    { id: 4, name: 'Payroll Tax Report - September 2025', type: 'Compliance', generatedAt: '2025-10-10 11:20:00', size: '1.1MB', status: 'completed', downloads: 1 },
    { id: 5, name: 'Annual Audit Package - 2024', type: 'Audit', generatedAt: '2025-10-09 13:30:00', size: '25.7MB', status: 'completed', downloads: 15 }
  ];

  // Mock data for scheduled reports
  const scheduledReports = [
    { id: 1, name: 'Monthly Financial Statements', frequency: 'Monthly', nextRun: '2025-11-01 09:00:00', recipients: ['CFO@company.com', 'finance@company.com'], status: 'active' },
    { id: 2, name: 'Weekly Cash Flow Summary', frequency: 'Weekly', nextRun: '2025-10-14 08:00:00', recipients: ['treasurer@company.com'], status: 'active' },
    { id: 3, name: 'Quarterly Tax Package', frequency: 'Quarterly', nextRun: '2026-01-01 10:00:00', recipients: ['tax@company.com', 'cpa@company.com'], status: 'active' },
    { id: 4, name: 'Daily Treasury Report', frequency: 'Daily', nextRun: '2025-10-12 07:30:00', recipients: ['treasury@company.com'], status: 'paused' }
  ];

  // Mock data for compliance deadlines
  const complianceDeadlines = [
    { id: 1, task: 'VAT Return Filing', deadline: '2025-10-31', status: 'pending', priority: 'high', department: 'Tax' },
    { id: 2, task: 'Monthly Payroll Tax', deadline: '2025-10-15', status: 'in-progress', priority: 'medium', department: 'HR/Payroll' },
    { id: 3, task: 'Annual Audit Preparation', deadline: '2025-12-15', status: 'not-started', priority: 'high', department: 'Finance' },
    { id: 4, task: 'Quarterly Board Report', deadline: '2025-10-20', status: 'draft', priority: 'medium', department: 'Executive' },
    { id: 5, task: 'Bank Reconciliation', deadline: '2025-10-12', status: 'overdue', priority: 'critical', department: 'Accounting' }
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleGenerateReport = (reportType) => {
    console.log(`Generating report: ${reportType}`);
    setActiveTab('generator');
  };

  const handleScheduleReport = () => {
    console.log('Opening report scheduler...');
    setActiveTab('scheduled');
  };

  const handleGoToAnalytics = () => {
    navigate('/financial-analytics-dashboard');
  };

  const handleBulkExport = () => {
    console.log('Initiating bulk export...');
    // Implement bulk export functionality
  };

  useEffect(() => {
    // Auto-refresh scheduled reports status
    const interval = setInterval(() => {
      console.log('Checking scheduled reports status...');
      // Update scheduled reports status
    }, 300000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: 'library', label: 'Report Library', icon: 'FolderOpen' },
    { id: 'generator', label: 'Generate Reports', icon: 'Settings' },
    { id: 'history', label: 'History', icon: 'Clock' },
    { id: 'scheduled', label: 'Scheduled', icon: 'Calendar' },
    { id: 'compliance', label: 'Compliance', icon: 'AlertTriangle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main 
        className={`transition-smooth pt-16 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Financial Reports Center</h1>
                  <p className="text-muted-foreground">Comprehensive financial reporting and document generation</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Download" onClick={handleBulkExport}>
                Bulk Export
              </Button>
              <Button variant="outline" iconName="Calendar" onClick={handleScheduleReport}>
                Schedule
              </Button>
              <Button variant="default" iconName="PieChart" onClick={handleGoToAnalytics}>
                Analytics
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border">
            <div className="flex space-x-8">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.id === 'compliance' && (
                    <span className="bg-error text-error-foreground text-xs px-2 py-0.5 rounded-full">
                      {complianceDeadlines?.filter(d => d?.status === 'overdue' || d?.priority === 'critical')?.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'library' && (
              <ReportLibrary 
                categories={reportCategories} 
                onGenerateReport={handleGenerateReport}
              />
            )}
            {activeTab === 'generator' && (
              <ReportGenerator 
                onReportGenerated={(report) => console.log('Report generated:', report)}
              />
            )}
            {activeTab === 'history' && (
              <ReportHistory 
                history={reportHistory} 
              />
            )}
            {activeTab === 'scheduled' && (
              <ScheduledReports 
                scheduledReports={scheduledReports} 
              />
            )}
            {activeTab === 'compliance' && (
              <ComplianceDeadlines 
                deadlines={complianceDeadlines} 
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinancialReportsCenter;